import React, { ReactNode } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { RotateCcw } from "lucide-react";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import { marketplaceFilters } from "@shared/slices/filters.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { NewLookChipsRow } from "features/newMarketplace/NewLook/filterPillStyles.ts";

interface NewLookFiltersBarProps {
  /** Controls placed before the chips (sort, full-list toggle on phones). */
  extraComponent?: ReactNode;
  /** Hides the shared drawer button (desktop shows every chip inline). */
  hideFiltersButton?: boolean;
  className?: string;
}

/**
 * The marketplace's filters as one row of pills with a quiet "Reset" once any
 * filter is active.
 */
const NewLookFiltersBar = ({
  extraComponent,
  hideFiltersButton,
  className,
}: NewLookFiltersBarProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const dispatch = useDispatch();
  const numActiveFilters = useSelector(
    marketplaceFilters.selectors.selectNumActiveFilters
  );

  return (
    <Row className={className}>
      <NewLookChipsRow>
        <FiltersChips
          filtersActions={marketplaceFilters.actions}
          filtersSelectors={marketplaceFilters.selectors}
          isSingleRowView={true}
          hideFiltersButton={hideFiltersButton}
          extraComponent={extraComponent}
        />
      </NewLookChipsRow>
      {!!numActiveFilters && (
        <ResetButton
          type="button"
          onClick={() =>
            dispatch(marketplaceFilters.actions.selectAll(undefined))
          }
        >
          <RotateCcw size={14} strokeWidth={1.75} />
          {t("filters.common.reset")}
        </ResetButton>
      )}
    </Row>
  );
};

export default NewLookFiltersBar;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const ResetButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: color 150ms, background-color 150ms;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 1px;
  }
`;
